import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Skeleton } from "@/components/ui/skeleton";
import { Church, Search, MapPin, User as UserIcon, Users, ShieldCheck } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function JemaatList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("ALL");

  useEffect(() => {
    api.get("/churches").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = items.filter((c) => {
    const matchQ = !q || (c.name + c.code + (c.short_name || "")).toLowerCase().includes(q.toLowerCase());
    const matchF = filter === "ALL" || c.verification_status === filter;
    return matchQ && matchF;
  });

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-xs tracking-[0.18em] uppercase text-muted-foreground">Organisasi</div>
          <h1 className="font-heading font-bold text-3xl text-foreground mt-1">Master Data Jemaat</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Kelola profil 8 Jemaat GPDP Klasis Mimika + 1 Pos PI Arwana Pomako. Data diverifikasi berjenjang oleh Sekretariat Klasis.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/jemaat/verifikasi" className="text-xs sm:text-sm font-semibold text-primary hover:underline flex items-center gap-1" data-testid="link-verifikasi-data">
            <ShieldCheck className="h-3.5 w-3.5" />
            Verifikasi Data
          </Link>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Cari jemaat…" className="pl-9 w-64"
              data-testid="jemaat-search"
            />
          </div>
        </div>
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="mb-6">
        <TabsList data-testid="jemaat-filter-tabs">
          <TabsTrigger value="ALL" data-testid="tab-all">Semua ({items.length})</TabsTrigger>
          <TabsTrigger value="VERIFIED" data-testid="tab-verified">Terverifikasi</TabsTrigger>
          <TabsTrigger value="SUBMITTED" data-testid="tab-submitted">Menunggu</TabsTrigger>
          <TabsTrigger value="REVISION_REQUIRED" data-testid="tab-revision">Revisi</TabsTrigger>
          <TabsTrigger value="UNVERIFIED" data-testid="tab-unverified">Belum</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-56" />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => <JemaatCard key={c.id} c={c} />)}
        </div>
      )}
    </AppShell>
  );
}

const CHURCH_PHOTO = "https://images.pexels.com/photos/15602359/pexels-photo-15602359.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function JemaatCard({ c }) {
  const initials = (c.short_name || c.name || "?").split(" ").slice(0,2).map(s=>s[0]).join("");
  return (
    <Link to={`/jemaat/${c.id}`} data-testid={`card-jemaat-${c.code}`}>
      <Card className="card-hover border-border/80 h-full overflow-hidden group">
        {/* Hero image */}
        <div className="relative h-32 bg-navy-gradient overflow-hidden">
          {c.photo_url ? (
            <img src={c.photo_url} alt={c.name} className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity" loading="lazy" />
          ) : (
            <img src={CHURCH_PHOTO} alt="" className="w-full h-full object-cover opacity-30 group-hover:opacity-40 transition-opacity" loading="lazy" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-primary/85 via-primary/30 to-transparent" />
          <div className="absolute top-3 right-3">
            <StatusBadge status={c.verification_status || "UNVERIFIED"} />
          </div>
          <div className="absolute bottom-3 left-4 right-4 text-white">
            <div className="text-[0.6rem] tracking-[0.22em] uppercase opacity-80 font-medium">{c.code}</div>
            <div className="font-heading font-bold text-lg leading-tight mt-0.5 line-clamp-1">{c.name}</div>
          </div>
          {c.type === "POS_PI" && (
            <div className="absolute top-3 left-3 status-badge bg-accent text-accent-foreground border-accent">Pos PI</div>
          )}
          {c.iuran_kategori && (
            <div className="absolute top-3 left-3 status-badge bg-accent text-accent-foreground border-accent" title={`Iuran Kategori ${c.iuran_kategori}`}>
              Kategori {c.iuran_kategori}
            </div>
          )}
        </div>
        <CardContent className="p-4">
          <div className="space-y-1.5 text-xs">
            <div className="flex items-center gap-2 text-muted-foreground">
              <UserIcon className="h-3.5 w-3.5 text-primary/60 shrink-0" />
              <span className="truncate font-medium text-foreground/80">{c.gembala_name || <em className="text-muted-foreground/70 font-normal">Gembala belum diisi</em>}</span>
            </div>
            <div className="flex items-center gap-2 text-muted-foreground">
              <MapPin className="h-3.5 w-3.5 text-primary/60 shrink-0" />
              <span className="truncate">{c.address || c.distrik || "Timika, Mimika"}</span>
            </div>
            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Users className="h-3.5 w-3.5 text-accent shrink-0" />
                <span className="font-semibold text-foreground">{c.jumlah_jiwa ? `${c.jumlah_jiwa} jiwa` : "—"}</span>
                {c.jumlah_kk && <span className="text-muted-foreground text-[0.7rem]">• {c.jumlah_kk} KK</span>}
              </div>
              <span className="text-[0.7rem] text-primary group-hover:text-secondary font-semibold transition-colors">Detail →</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function EmptyState() {
  return (
    <div className="border border-dashed border-border rounded-lg p-12 text-center">
      <Church className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
      <div className="text-sm text-muted-foreground">Tidak ada jemaat yang cocok dengan filter Anda.</div>
    </div>
  );
}
